import React from 'react';
import {
    List,
    TextField,
    ReferenceField,
    ReferenceInput,
    AutocompleteInput,
    Filter,
    ShowButton,
    EditButton,
    SelectInput,
    TextInput,
    FunctionField,
    DateField,
    Link,
} from 'react-admin';

import CountryField from '../components/CountryField';
import CustomizableGrid from '../components/CustomizableGrid';

const LocationFilter = props => (
    <Filter variant="standard" {...props}>
        <TextInput label="Search" source="q" alwaysOn />

        <ReferenceInput
            source="warehouseId"
            reference="warehouses"
            resource="warehouses"
            label="Warehouse"
        >
            <AutocompleteInput
                shouldRenderSuggestions={val => val.trim().length > 2}
                optionText={choice => `${choice.name}`}
            />
        </ReferenceInput>
        <ReferenceInput
            source="countryId"
            reference="country"
            resource="country"
            label="Country"
        >
            <SelectInput optionText={choice => `${choice.name}`} />
        </ReferenceInput>
    </Filter>
);

// Custom field components
const WarehouseField = ({ record }) => {
    if (record && record.warehouseId) {
        return (
            <Link to={`/warehouses/${record.warehouseId}/show`}>
                {record.warehouse
                    ? record.warehouse.name
                    : `Warehouse ${record.warehouseId}`}
            </Link>
        );
    }
    return null;
};

const StorageRecordsCountField = ({ record }) => {
    if (record && record.storagerecords) {
        return record.storagerecords.length;
    }
    return 0;
};

const LocationList = props => {
    const defaultColumns = [
        // Default visible columns (essential information)
        {
            id: 'serial',
            label: 'Serial',
            visible: true,
            sortable: false,
        },
        {
            id: 'id',
            label: 'ID',
            visible: true,
        },
        {
            id: 'path',
            label: 'Path',
            visible: true,
        },
        {
            id: 'area',
            label: 'Area',
            visible: true,
        },
        {
            id: 'aisle',
            label: 'Aisle',
            visible: true,
        },
        {
            id: 'x',
            label: 'X',
            visible: true,
        },
        {
            id: 'y',
            label: 'Y',
            visible: true,
        },
        {
            id: 'capacity',
            label: 'Capacity',
            visible: true,
        },
        {
            id: 'freeVolume',
            label: 'Free Volume',
            visible: true,
        },
        {
            id: 'usedVolume',
            label: 'Used Volume',
            visible: true,
        },
        {
            id: 'warehouse',
            label: 'Warehouse',
            visible: true,
        },
        {
            id: 'show',
            label: 'Show',
            visible: true,
            sortable: false,
        },

        // Optional columns (visible: false, can be enabled by users)
        {
            id: 'createdAt',
            label: 'Created At',
            visible: false,
        },
        {
            id: 'updatedAt',
            label: 'Updated At',
            visible: false,
        },
        {
            id: 'deletedAt',
            label: 'Deleted At',
            visible: false,
        },
        {
            id: 'z',
            label: 'Z',
            visible: false,
        },
        {
            id: 'width',
            label: 'Width',
            visible: false,
        },
        {
            id: 'height',
            label: 'Height',
            visible: false,
        },
        {
            id: 'length',
            label: 'Length',
            visible: false,
        },
        {
            id: 'warehouseId',
            label: 'Warehouse ID',
            visible: false,
        },
        {
            id: 'countryId',
            label: 'Country ID',
            visible: false,
        },
        {
            id: 'country',
            label: 'Country',
            visible: false,
        },
        {
            id: 'storageRecordsCount',
            label: 'Storage Records Count',
            visible: false,
        },
        // Storage record details from nested storagerecords array
        {
            id: 'firstStorageRecordId',
            label: 'First Storage Record ID',
            visible: false,
        },
        {
            id: 'firstStorageRecordType',
            label: 'First Storage Record Type',
            visible: false,
        },
        {
            id: 'firstStorageRecordStatus',
            label: 'First Storage Record Status',
            visible: false,
        },
        {
            id: 'firstStorageRecordFrom',
            label: 'First Storage Record From',
            visible: false,
        },
        {
            id: 'firstStorageRecordTo',
            label: 'First Storage Record To',
            visible: false,
        },
        {
            id: 'firstStorageRecordQty',
            label: 'First Storage Record Qty',
            visible: false,
        },
        {
            id: 'firstStorageId',
            label: 'First Storage ID',
            visible: false,
        },
        {
            id: 'firstOrderId',
            label: 'First Order ID',
            visible: false,
        },
        {
            id: 'edit',
            label: 'Edit',
            visible: false,
            sortable: false,
        },
    ];

    const renderColumn = columnId => {
        switch (columnId) {
            case 'serial':
                return <TextField source="serial" sortable={false} />;
            case 'id':
                return <TextField source="id" />;
            case 'path':
                return <TextField source="path" />;
            case 'area':
                return <TextField source="area" />;
            case 'aisle':
                return <TextField source="aisle" />;
            case 'x':
                return <TextField source="x" />;
            case 'y':
                return <TextField source="y" />;
            case 'z':
                return <TextField source="z" />;
            case 'width':
                return <TextField source="width" />;
            case 'height':
                return <TextField source="height" />;
            case 'length':
                return <TextField source="length" />;
            case 'capacity':
                return <TextField source="capacity" />;
            case 'freeVolume':
                return <TextField source="freeVolume" label="Free Volume" />;
            case 'usedVolume':
                return <TextField source="usedVolume" label="Used Volume" />;
            case 'warehouse':
                return (
                    <FunctionField
                        label="Warehouse"
                        render={record => <WarehouseField record={record} />}
                    />
                );
            case 'country':
                return <CountryField />;
            case 'show':
                return <ShowButton style={{ padding: 0 }} />;
            case 'edit':
                return <EditButton color="secondary" style={{ padding: 0 }} />;
            case 'createdAt':
                return <DateField source="createdAt" showTime />;
            case 'updatedAt':
                return <DateField source="updatedAt" showTime />;
            case 'deletedAt':
                return <DateField source="deletedAt" showTime />;
            case 'warehouseId':
                return <TextField source="warehouseId" />;
            case 'countryId':
                return <TextField source="countryId" />;
            case 'storageRecordsCount':
                return (
                    <FunctionField
                        label="Storage Records Count"
                        render={record => (
                            <StorageRecordsCountField record={record} />
                        )}
                    />
                );
            case 'firstStorageRecordId':
                return (
                    <FunctionField
                        label="First Storage Record ID"
                        render={record =>
                            record.storagerecords &&
                            record.storagerecords.length > 0
                                ? record.storagerecords[0].id
                                : ''
                        }
                    />
                );
            case 'firstStorageRecordType':
                return (
                    <FunctionField
                        label="First Storage Record Type"
                        render={record =>
                            record.storagerecords &&
                            record.storagerecords.length > 0
                                ? record.storagerecords[0].type
                                : ''
                        }
                    />
                );
            case 'firstStorageRecordStatus':
                return (
                    <FunctionField
                        label="First Storage Record Status"
                        render={record =>
                            record.storagerecords &&
                            record.storagerecords.length > 0
                                ? record.storagerecords[0].status
                                : ''
                        }
                    />
                );
            case 'firstStorageRecordFrom':
                return (
                    <FunctionField
                        label="First Storage Record From"
                        render={record =>
                            record.storagerecords &&
                            record.storagerecords.length > 0 &&
                            record.storagerecords[0].from
                                ? new Date(
                                      record.storagerecords[0].from
                                  ).toLocaleString()
                                : ''
                        }
                    />
                );
            case 'firstStorageRecordTo':
                return (
                    <FunctionField
                        label="First Storage Record To"
                        render={record =>
                            record.storagerecords &&
                            record.storagerecords.length > 0 &&
                            record.storagerecords[0].to
                                ? new Date(
                                      record.storagerecords[0].to
                                  ).toLocaleString()
                                : ''
                        }
                    />
                );
            case 'firstStorageRecordQty':
                return (
                    <FunctionField
                        label="First Storage Record Qty"
                        render={record =>
                            record.storagerecords &&
                            record.storagerecords.length > 0
                                ? record.storagerecords[0].qty
                                : ''
                        }
                    />
                );
            case 'firstStorageId':
                return (
                    <FunctionField
                        label="First Storage ID"
                        render={record =>
                            record.storagerecords &&
                            record.storagerecords.length > 0
                                ? record.storagerecords[0].storageId
                                : ''
                        }
                    />
                );
            case 'firstOrderId':
                return (
                    <FunctionField
                        label="First Order ID"
                        render={record =>
                            record.storagerecords &&
                            record.storagerecords.length > 0
                                ? record.storagerecords[0].orderId
                                : ''
                        }
                    />
                );
            default:
                return null;
        }
    };

    return (
        <CustomizableGrid
            title="Warehouse Locations"
            defaultColumns={defaultColumns}
            storageKey="locations-columns"
            renderColumn={renderColumn}
            bulkActionButtons={false}
            filtersComponent={<LocationFilter />}
            permissions={[]}
            {...props}
        />
    );
};

LocationList.defaultProps = {
    resource: 'locations',
    location: { search: '' },
};

export default LocationList;
